/**
 * Discount calculation utilities for Stay pricing
 */

export interface DiscountCalculation {
  originalRate: number;
  discountedRate: number;
  discountPercent: number;
  savingsPerNight: number;
  nights: number;
  originalTotal: number;
  discountedTotal: number;
  savings: number;
  deposit10Percent: number;
  balance90Percent: number;
}

/**
 * Calculate discount percentage from original and discounted rates
 */
export function calculateDiscountPercent(original: number, discounted: number): number {
  if (original <= 0 || discounted < 0) return 0;
  if (discounted >= original) return 0;
  const percent = ((original - discounted) / original) * 100;
  return Math.round(percent); // Round to the nearest whole percent for UI/summary display
}

/**
 * Calculate all discount-related amounts for a booking
 */
export function calculateDiscountBreakdown(
  originalRate: number,
  discountedRate: number,
  nights: number
): DiscountCalculation {
  const discountPercent = calculateDiscountPercent(originalRate, discountedRate);
  const savingsPerNight = Math.max(0, originalRate - discountedRate);
  const originalTotal = originalRate * nights;
  const discountedTotal = discountedRate * nights;
  const savings = originalTotal - discountedTotal;
  
  // 10% deposit on DISCOUNTED total only
  const deposit10Percent = Math.round((discountedTotal * 0.1) * 100) / 100;
  const balance90Percent = Math.round((discountedTotal * 0.9) * 100) / 100;

  return {
    originalRate,
    discountedRate,
    discountPercent,
    savingsPerNight,
    nights,
    originalTotal: Math.round(originalTotal * 100) / 100,
    discountedTotal: Math.round(discountedTotal * 100) / 100,
    savings: Math.round(savings * 100) / 100,
    deposit10Percent,
    balance90Percent,
  };
}

/**
 * Format discount display string (e.g., "Save 15% (R127/night)")
 */
export function formatDiscountDisplay(original: number, discounted: number): string {
  const percent = calculateDiscountPercent(original, discounted);
  const savings = original - discounted;
  
  if (percent <= 0 || savings <= 0) return '';
  
  return `Save ${percent}% (R${savings.toFixed(2)}/night)`;
}

/**
 * Format savings amount for display (e.g., "Save R255")
 */
export function formatSavingsDisplay(original: number, discounted: number, nights: number): string {
  const originalTotal = original * nights;
  const discountedTotal = discounted * nights;
  const savings = originalTotal - discountedTotal;
  
  if (savings <= 0) return '';
  
  return `Save R${savings.toFixed(2)}`;
}

/**
 * Check if a profile has an active discount
 */
export function hasActiveDiscount(
  currentRate?: number,
  originalRate?: number,
  hasDiscountFlag?: boolean
): boolean {
  // Must have explicit flag set
  if (!hasDiscountFlag) return false;
  
  // Must have both rates
  if (!currentRate || !originalRate) return false;
  
  // Current must be less than original
  if (currentRate >= originalRate) return false;
  
  return true;
}
